import _isEmpty from "lodash/isEmpty";
import _isFunction from "lodash/isFunction";
import moment from "moment";
import { idnr } from "@navikt/fnrvalidator";
import { ValidationRule } from "../../../models";
import { isRunningOnClientSide } from "../../../shared/util";

export const MAX_PRICE_FOR_LEASE_RESELLER = 30000;

export const VALIDATION_FIELD = {
    SUBSCRIPTION_NUMBER: "subscriptionNumber",
    SUBSCRIPTION_MBB_NUMBER: "subscriptionMBBNumber",
    FIRST_NAME: "firstName",
    LAST_NAME: "lastName",
    STREET: "street",
    STREET_CO: "streetCO",
    PB_NUMBER: "boxNumber",
    PB_NAME: "boxName",
    POSTCODE: "postcode",
    CITY: "city",
    PHONE: "phone",
    EMAIL: "email",
    SSN: "ssn",
    BIRTHDATE: "birthDate",
    PRODUCT_USER_FIRST_NAME: "productUserFirstName",
    PRODUCT_USER_LAST_NAME: "productUserLastName",
    PRODUCT_USER_STREET: "productUserStreet",
    PRODUCT_USER_CITY: "productUserCity",
    PRODUCT_USER_POSTCODE: "productUserPostcode",
    PRODUCT_USER_PHONE: "productUserPhone",
    PRODUCT_USER_PHONE_NON_REQUIRED: "productUserNewPhone",
    PRODUCT_USER_EMAIL: "productUserEmail",
    PRODUCT_USER_BIRTHDATE: "productUserBirthDate",
    CONTACT_PHONE: "contactPhone",
    CONTACT_PHONE_NON_REQUIRED: "contactPhoneNonRequired",
    CONTACT_EMAIL: "contactEmail",
    PREPAID_SIM_NUMBER: "prepaidSimNumber",
    BANK_ID_FIRST_NAME: "bankIDFirstName",
    BANK_ID_LAST_NAME: "bankIDLastName",
    PASSPORT_NUMBER: "passportNumber",
    DRIVERS_LICENSE: "driversLicense",
    BANK_ID: "bankId",
    NATIONAL_ID_CARD: "nationalId",
    EAN: "ean",
    IMEI: "imei",
    PHONE_OR_MBB: "phoneOrMbb",
    HARDWARE_MODEL: "hardwareModel",
    HARDWARE_PRICE: "hardwarePrice",
    HARDWARE_COMMITMENT_DURATION: "hardwareCommitmentDuration",
} as const;

// If they enter 04.02.85, convert it to 04.02.1985
export const expandShortYear = (date: string): string => {
    const [dd, mm, yy] = date.split(".");
    const shortYear = parseInt(yy, 10);
    const candidate = moment(`${dd}.${mm}.${2000 + shortYear}`, "DD.MM.YYYY", true);
    const now = moment();
    if (candidate.isValid() && !candidate.isAfter(now)) {
        return `${dd}.${mm}.${2000 + shortYear}`;
    }
    return `${dd}.${mm}.${1900 + shortYear}`;
};

export const getFormattedDate = (enteredDate: string, previousBirthdateLength = 0): string => {
    // Adds/removes the dots between mm.dd.yyyy in the birthdate field as the user types
    // to make validation simpler for both us and the user
    let formattedDate = (enteredDate || "").replace(/[^0-9.]/g, "");
    const birthdateLength = formattedDate.length;
    if (!/\d*\.\d*\./.test(formattedDate) && /(\d{3})/.test(formattedDate)) {
        formattedDate = formattedDate.replace(/(\d{2})(\d$)/, "$1.$2");
        // Only do this if user removed a character, to allow users to enter dots manually
    } else if (/\d{2}\.$/.test(formattedDate) && birthdateLength < previousBirthdateLength) {
        formattedDate = formattedDate.replace(/(.*)(\d{2})(\.)$/, "$1$2");
    }
    return formattedDate.substr(0, 10);
};

export const getFormattedPhoneNumber = (number?: string): string => {
    let onlyDigits = (number || "").replace(/\D/g, "");
    onlyDigits = onlyDigits.replace(/(\d{1,3})(\d{0,2})(\d{0,3})/, "$1 $2 $3").trim();
    return onlyDigits.substr(0, 10);
};

export const getFormattedPrepaidSimNumber = (number?: string): string => {
    let onlyDigits = (number || "").replace(/\D/g, "");
    onlyDigits = onlyDigits.replace(/(\d{1,3})/, "$1").trim();
    return onlyDigits.substr(0, 3);
};

export const getFormattedMBBNumber = (number: string): string => {
    let onlyDigits = (number || "").replace(/\s/g, "");
    onlyDigits = onlyDigits.replace(/(\d{1,4})(\d{0,4})(\d{0,4})/, "$1 $2 $3").trim();
    return onlyDigits.substr(0, 14);
};

export const getFormattedPhoneOrMBBNumber = (number?: string): string => {
    const onlyDigits = (number || "").replace(/\D/g, "").substr(0, 12);
    if (onlyDigits.length > 8) {
        return onlyDigits.replace(/(\d{1,4})(\d{0,4})(\d{0,4})/, "$1 $2 $3").trim();
    }
    return onlyDigits.replace(/(\d{1,3})(\d{0,2})(\d{0,3})/, "$1 $2 $3").trim();
};

const isValidImeiLuhn = (imei: string): boolean => {
    if (!/^\d{15}$/.test(imei)) {
        return false;
    }

    let sum = 0;

    // Starting from the right, every second digit is doubled.
    // If doubling results in a two-digit number, add both digits (equivalent to subtracting 9).
    for (let index = 0; index < imei.length; index += 1) {
        let digit = Number(imei[index]);
        const shouldDouble = index % 2 === 1;

        if (shouldDouble) {
            digit *= 2;
            if (digit > 9) {
                digit -= 9;
            }
        }

        sum += digit;
    }

    return sum % 10 === 0;
};

const D_NUMBER_TEST_USER = (isRunningOnClientSide() && window.nettbutikkConfig?.dNumberTestUser) || "";

export const validationRulesTemplate: { [type: string]: ValidationRule } = {
    street: {
        required: true,
    },
    streetCO: {
        required: false,
    },
    postcode: {
        regex: /^\d{4}$/,
        required: true,
        errorMessage: "Postnummer må bestå av 4 siffer.",
        mask: (code: string) => {
            const onlyDigits = (code || "").replace(/\D/g, "");
            return onlyDigits.substr(0, 4);
        },
    },
    city: {
        regex: /^[a-zA-ZæøåÆØÅ]+( [a-zA-ZæøåÆØÅ]+)*$/,
        required: true,
        errorMessage: "Poststed skal kun bestå av bokstaver og mellomrom.",
    },
    phone: {
        regex: /^(\+47)?\d{8}$/,
        required: true,
        errorMessage: "Nummeret må inneholde nøyaktig 8 siffer.",
    },
    email: {
        regex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        required: true,
        errorMessage: "E-post er ikke en korrekt e-post adresse.",
    },
    ssn: {
        required: true,
        mask: (ssn: string) => {
            const onlyDigits = (ssn || "").replace(/\D/g, "");
            return onlyDigits.substr(0, 11);
        },
        validateFn: (ssn: string) => {
            const strippedSSN = (ssn || "").replace(/\s/g, "");

            if (strippedSSN.length !== 11) {
                return "Fødsels-/D-nummer må bestå av 11 siffer.";
            }

            if (strippedSSN === D_NUMBER_TEST_USER) {
                return null;
            }

            if (idnr(strippedSSN).status !== "valid") {
                return "Ugyldig fødselsnummer eller D-nummer.";
            }

            return null;
        },
    },
    orgNumber: {
        regex: /^\d{9}$/,
        required: true,
        errorMessage: "Organisasjonsnummer må bestå av 9 siffer.",
    },
    date: {
        mask: (newValue: string, oldValue?: string) => {
            const formatted = getFormattedDate(newValue, oldValue?.length);
            if (newValue === oldValue && /^\d{2}\.\d{2}\.\d{2}$/.test(formatted)) {
                return expandShortYear(formatted);
            }
            return formatted;
        },
        validateFn: (d: string) => {
            const shortDateRegex = /^\d{2}\.\d{2}\.\d{2}$/;
            const validDateRegex = /^\d{2}\.\d{2}\.\d{4}$/;
            const firstSingleDigitRegex = /^(\d)\./;
            const secondSingleDigitRegex = /\.(\d)\.(?!$)/;

            if (firstSingleDigitRegex.test(d) || secondSingleDigitRegex.test(d)) {
                const suggestedDate = d.replace(firstSingleDigitRegex, "0$1.").replace(secondSingleDigitRegex, ".0$1.");
                return `Fødselsdato må ha formatet dd.mm.åååå. Mente du ${suggestedDate}?`;
            }

            // Expand short year before validating (in case mask hasn't run yet)
            const dateToValidate = shortDateRegex.test(d) ? expandShortYear(d) : d;

            if (!validDateRegex.test(dateToValidate)) {
                return "Fødselsdato må ha formatet dd.mm.åååå";
            }

            const now = moment();
            const enteredDate = moment(dateToValidate, "DD.MM.YYYY");

            if (!enteredDate.isValid()) {
                return "Dagen eller måneden du har skrevet inn er ugyldig";
            }

            if (enteredDate.isAfter(now)) {
                if (enteredDate.isBefore(moment("01.01.2100", "DD.MM.YYYY"))) {
                    // Many erroneous orders seems like users wrote 20xx when they meant 19xx. This suggestion makes it easier to see the error.
                    return `Fødselsdato kan ikke være i fremtiden. Mente du ${enteredDate
                        .subtract("100", "years")
                        .format("DD.MM.YYYY")}?`;
                } else {
                    return `Fødselsdato kan ikke være i fremtiden.`;
                }
            }

            return null;
        },
        required: false,
    },
};

export const allValidationRules: { [rule: string]: ValidationRule } = {
    [VALIDATION_FIELD.SUBSCRIPTION_NUMBER]: {
        regex: /^\d{10}$/, // Need to allow 10 digits as the number is formatted XXX XX XXX
        required: true,
        mask: getFormattedPhoneNumber,
        errorMessage: "Nummeret må inneholde nøyaktig 8 siffer.",
    },
    [VALIDATION_FIELD.SUBSCRIPTION_MBB_NUMBER]: {
        regex: /^\d{12}$/,
        required: true,
        mask: getFormattedMBBNumber,
        errorMessage: "Nummeret må inneholde nøyaktig 12 siffer.",
    },
    [VALIDATION_FIELD.PHONE_OR_MBB]: {
        required: true,
        mask: getFormattedPhoneOrMBBNumber,
        validateFn: (value: string) => {
            const digits = value.replace(/\s/g, "");
            if (digits.length === 8 || digits.length === 12) return null;
            return "Nummeret må inneholde 8 (telefon) eller 12 (MBB) siffer.";
        },
    },
    [VALIDATION_FIELD.FIRST_NAME]: {
        required: true,
    },
    [VALIDATION_FIELD.LAST_NAME]: {
        required: true,
    },
    [VALIDATION_FIELD.STREET]: validationRulesTemplate.street,
    [VALIDATION_FIELD.PB_NAME]: {
        required: false,
    },
    [VALIDATION_FIELD.PB_NUMBER]: {
        required: true,
    },
    [VALIDATION_FIELD.POSTCODE]: validationRulesTemplate.postcode,
    [VALIDATION_FIELD.CITY]: {
        required: true,
    },
    [VALIDATION_FIELD.PHONE]: {
        regex: /^[0-9]{3} ?[0-9]{2} ?[0-9]{3}$/,
        required: true,
        mask: getFormattedPhoneNumber,
        errorMessage: "Nummeret må inneholde nøyaktig 8 siffer.",
    },
    [VALIDATION_FIELD.PREPAID_SIM_NUMBER]: {
        regex: /^[0-9]{3}/,
        required: true,
        mask: getFormattedPrepaidSimNumber,
        errorMessage: "",
    },
    [VALIDATION_FIELD.EMAIL]: validationRulesTemplate.email,
    [VALIDATION_FIELD.SSN]: validationRulesTemplate.ssn,
    [VALIDATION_FIELD.BIRTHDATE]: {
        ...validationRulesTemplate.date,
        required: true,
    },
    [VALIDATION_FIELD.PRODUCT_USER_FIRST_NAME]: {
        required: true,
    },
    [VALIDATION_FIELD.PRODUCT_USER_LAST_NAME]: {
        required: true,
    },
    [VALIDATION_FIELD.PRODUCT_USER_STREET]: {
        ...validationRulesTemplate.street,
        required: false,
    },
    [VALIDATION_FIELD.PRODUCT_USER_CITY]: {
        required: false,
    },
    [VALIDATION_FIELD.PRODUCT_USER_POSTCODE]: {
        ...validationRulesTemplate.postcode,
        required: false,
    },
    [VALIDATION_FIELD.PRODUCT_USER_PHONE]: {
        regex: /^[0-9]{3} ?[0-9]{2} ?[0-9]{3}$/,
        required: true,
        mask: getFormattedPhoneNumber,
        errorMessage: "Nummeret må inneholde nøyaktig 8 siffer.",
    },
    [VALIDATION_FIELD.PRODUCT_USER_EMAIL]: {
        ...validationRulesTemplate.email,
        required: false,
    },
    [VALIDATION_FIELD.PRODUCT_USER_BIRTHDATE]: {
        ...validationRulesTemplate.date,
        required: true,
    },
    [VALIDATION_FIELD.CONTACT_PHONE]: {
        regex: /^(\+47)?[49][0-9]{2} ?[0-9]{2} ?[0-9]{3}$/,
        required: true,
        mask: getFormattedPhoneNumber,
        errorMessage: "Nummeret må inneholde nøyaktig 8 siffer og starte med 4 eller 9.",
    },
    [VALIDATION_FIELD.CONTACT_PHONE_NON_REQUIRED]: {
        regex: /^(\+47)?[49][0-9]{2} ?[0-9]{2} ?[0-9]{3}$/,
        required: false,
        mask: getFormattedPhoneNumber,
        errorMessage: "Nummeret må inneholde nøyaktig 8 siffer og starte med 4 eller 9.",
    },
    [VALIDATION_FIELD.CONTACT_EMAIL]: validationRulesTemplate.email,
    [VALIDATION_FIELD.BANK_ID_FIRST_NAME]: {
        required: true,
    },
    [VALIDATION_FIELD.BANK_ID_LAST_NAME]: {
        required: true,
    },
    [VALIDATION_FIELD.PASSPORT_NUMBER]: {
        regex: /^[a-zA-Z0-9]{5,30}$/,
        required: true,
        errorMessage: "Passnummer må være mellom 5 og 30 alfanumeriske tegn.",
    },
    [VALIDATION_FIELD.DRIVERS_LICENSE]: {
        regex: /^[a-zA-Z0-9]{6,20}$/,
        required: true,
        errorMessage: "Førerkortnummer må være mellom 6 og 20 alfanumeriske tegn.",
    },
    [VALIDATION_FIELD.BANK_ID]: {
        validateFn: (value: string) => {
            const phoneRegex = /^(\+47)?\d{8}$/;
            const emailRegex =
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

            const isPhoneNumber = phoneRegex.test(value);
            const isEmail = emailRegex.test(value);

            if (!isPhoneNumber && !isEmail) {
                return "Verdien må være et gyldig telefonnummer eller e-postadresse.";
            }

            return null;
        },
        required: true,
    },
    [VALIDATION_FIELD.NATIONAL_ID_CARD]: {
        regex: /^[a-zA-Z0-9]{9}$/,
        required: true,
        errorMessage: "Nasjonalt ID-kortnummer må være nøyaktig 9 alfanumeriske tegn.",
    },
    [VALIDATION_FIELD.EAN]: {
        regex: /^\d{8}$|^\d{12,14}$/,
        errorMessage: "EAN/GTIN må være 8, 12, 13 eller 14 siffer.",
        mask: (ean: string) => {
            const onlyDigits = (ean || "").replace(/\D/g, "");
            return onlyDigits.substr(0, 14);
        },
    },
    [VALIDATION_FIELD.IMEI]: {
        regex: /^\d{15}$/,
        errorMessage: "IMEI må være nøyaktig 15 siffer.",
        validateFn: (imei: string) => {
            if (!isValidImeiLuhn(imei)) {
                return "IMEI er ugyldig.";
            }
            return null;
        },
        mask: (imei: string) => {
            const onlyDigits = (imei || "").replace(/\D/g, "");
            return onlyDigits.substr(0, 15);
        },
    },
    [VALIDATION_FIELD.HARDWARE_MODEL]: {
        required: true,
    },
    [VALIDATION_FIELD.HARDWARE_PRICE]: {
        required: true,
        regex: /^[+-]?([0-9]*[.])?[0-9]+$|^[+-]?[0-9]+([.][0-9]*)?$/,
        errorMessage: "Pris må være et gyldig tall.",
        validateFn: (price: string) => {
            if (parseFloat(price) > MAX_PRICE_FOR_LEASE_RESELLER) {
                return `Pris kan ikke overstige ${MAX_PRICE_FOR_LEASE_RESELLER.toLocaleString("nb-NO")} kr.`;
            }
            return null;
        },
        mask: (price: string) => {
            const onlyDigits = (price || "").replace(/\D/g, "");
            return onlyDigits;
        },
    },
    [VALIDATION_FIELD.HARDWARE_COMMITMENT_DURATION]: {
        required: true,
        mask: (imei: string) => {
            const onlyDigits = (imei || "").replace(/\D/g, "");
            return onlyDigits;
        },
    },
};

export const maskField = (inputData: string, oldData: string, rule: ValidationRule): string => {
    const inputDataString = inputData || "";
    const oldDataString = oldData || "";

    if (!rule || !_isFunction(rule.mask)) {
        return inputDataString;
    }

    return rule.mask(inputDataString, oldDataString);
};

/**
   Returns null if passed validation
 */
export const validateField = (inputData: string, rule: ValidationRule) => {
    const inputDataString = inputData;
    if (!rule) {
        return null;
    }

    if (_isEmpty(inputDataString)) {
        if (rule.required) {
            return "Dette feltet er obligatorisk.";
        } else {
            return null;
        }
    }

    if (rule.regex && !rule.regex.test(inputDataString)) {
        return rule.errorMessage;
    }
    if (rule.validateFn) {
        const validateResult = rule.validateFn(inputDataString);
        if (validateResult !== null) {
            return validateResult;
        }
    }

    return null;
};
